/**
 * Bloco "Linha do tempo" do Detalhe do Ambiente (Lote A — auditoria §2.3).
 *
 * Fonte: GET /api/ambientes-producao/{id}/timeline?limite=N — endpoint
 * agregador que junta as 4 fontes (auditoria por alvo + bipagens + atrasos
 * + marcos do lote) já ordenadas. Aqui só se apresenta:
 *   - timeline vertical (ícone por tipo · título · detalhe · ator ·
 *     dd/mmm hh:mm), mais recente primeiro;
 *   - `bipagens_etapa` chega AGRUPADO (D5.1): linha-resumo
 *     "Corte — 8 peças · 14/jul 08:12 → 16/jul 15:40 · 26 carimbos" com
 *     expandir/colapsar dos carimbos individuais (operador, ação,
 *     setup/refugo quando houver);
 *   - "Carregar mais antigos" quando `omitidos > 0` (repete a chamada com
 *     limite maior — sem paginação por cursor no MVP).
 *
 * `gatilhoRecarga` recarrega a timeline quando o Detalhe substitui o
 * ambiente após uma action (congelar, atraso, etapa…) — o evento novo
 * aparece sem F5.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiCircle,
  FiClock,
  FiCloudSnow,
  FiEdit2,
  FiFlag,
  FiLayers,
  FiPackage,
  FiPlay,
  FiRefreshCw,
  FiSun,
  FiTarget,
  FiUserPlus,
  FiXCircle,
} from "react-icons/fi";
import styled from "styled-components";

import { useAuth } from "../../../context/AuthContext";
import { ETAPA_LABELS, timelineAmbiente } from "../../../services/producaoV2";
import { formatarDataHoraCurta } from "../../../utils/dataHora";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SecondaryButton,
  SectionTitle,
} from "../../Orcamentos/styles";
import { LinhaErro, Mono, NotaDiscreta, Shimmer } from "../_shared/ui";

const LIMITE_INICIAL = 200;
const INCREMENTO_LIMITE = 200;

/**
 * Ícone + tom por tipo de evento (contrato do endpoint). `icone_hint` do
 * backend serve de fallback para tipos que este build ainda não conhece —
 * evento desconhecido nunca some, cai no ícone genérico neutro.
 */
const INFO_TIPO = {
  ambiente_criado: { icone: FiFlag, tone: "info" },
  etapa_iniciada: { icone: FiPlay, tone: "info" },
  etapa_concluida: { icone: FiCheckCircle, tone: "success" },
  bipagens_etapa: { icone: FiTarget, tone: "info" },
  atraso_registrado: { icone: FiClock, tone: "danger" },
  congelado: { icone: FiCloudSnow, tone: "warning" },
  descongelado: { icone: FiSun, tone: "success" },
  cancelado: { icone: FiXCircle, tone: "danger" },
  etapa_alocada: { icone: FiUserPlus, tone: "info" },
  etapa_realocada: { icone: FiRefreshCw, tone: "info" },
  lote_criado: { icone: FiLayers, tone: "info" },
  lote_liberado: { icone: FiPackage, tone: "success" },
  editado: { icone: FiEdit2, tone: "neutral" },
};

const ACAO_CARIMBO_LABELS = {
  INICIAR: "iniciou",
  CONCLUIR: "concluiu",
  PULAR: "pulou",
};

const COR_TONE = {
  success: "var(--success-text)",
  warning: "var(--warning-text)",
  danger: "var(--danger-text)",
  info: "var(--info-text)",
  neutral: "var(--ink-3)",
};

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Trilha = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;

  /* linha vertical contínua atrás dos pontos */
  &::before {
    content: "";
    position: absolute;
    left: 13px;
    top: 8px;
    bottom: 8px;
    width: 2px;
    background: var(--border-subtle);
  }
`;

const ItemEvento = styled.li`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 7px 0;
  min-width: 0;

  .ponto {
    position: relative;
    z-index: 1;
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: ${({ $tone }) => COR_TONE[$tone] || COR_TONE.neutral};
  }

  .conteudo {
    flex: 1 1 auto;
    min-width: 0;
    padding-top: 3px;
  }

  .topo {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 10px;
    min-width: 0;
  }

  .titulo {
    font-size: 13px;
    font-weight: 600;
    color: var(--ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .quando {
    flex: 0 0 auto;
    font-size: 11px;
    color: var(--ink-3);
  }

  .detalhe {
    font-size: 12px;
    color: var(--ink-2);
    margin: 2px 0 0;
    overflow-wrap: anywhere;
  }

  .ator {
    display: block;
    font-size: 11px;
    color: var(--ink-4);
    margin-top: 2px;
  }
`;

/** Linha-resumo clicável do grupo de bipagens (expande os carimbos). */
const ResumoBipagens = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: none;
  padding: 2px 0;
  margin-top: 2px;
  font: inherit;
  font-size: 12px;
  color: var(--ink-2);
  cursor: pointer;
  max-width: 100%;
  min-width: 0;

  &:hover {
    color: var(--ink);
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
    border-radius: 6px;
  }

  .resumo {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ListaCarimbos = styled.ul`
  list-style: none;
  margin: 6px 0 2px;
  padding: 0 0 0 10px;
  border-left: 2px solid var(--border-subtle);

  li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
    font-size: 12px;
    color: var(--ink-2);
    min-width: 0;
  }

  li .quem {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RodapeTimeline = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function infoDoEvento(ev) {
  return (
    INFO_TIPO[ev.tipo]
    || INFO_TIPO[ev.icone_hint]
    || { icone: FiCircle, tone: "neutral" }
  );
}

function rotuloEtapaBipagem(dados) {
  const codigo = dados?.etapa;
  return (codigo && (ETAPA_LABELS[codigo] || codigo)) || "Bipagens";
}

/** "Corte — 8 peças · 14/jul 08:12 → 16/jul 15:40 · 26 carimbos". */
function resumoBipagens(dados) {
  const r = dados?.resumo || {};
  const partes = [];
  if (r.pecas != null) partes.push(`${r.pecas} peça(s)`);
  if (r.primeira || r.ultima) {
    partes.push(
      `${formatarDataHoraCurta(r.primeira)} → ${formatarDataHoraCurta(r.ultima)}`,
    );
  }
  const carimbos = r.eventos ?? (dados?.carimbos || []).length;
  partes.push(`${carimbos} carimbo(s)`);
  return partes.join(" · ");
}

function rotuloCarimbo(carimbo) {
  const acao = carimbo?.acao;
  return ACAO_CARIMBO_LABELS[acao] || (acao ? String(acao).toLowerCase() : "bipou");
}

// ---------------------------------------------------------------------------
// Sub-blocos
// ---------------------------------------------------------------------------

function CarimbosDoGrupo({ carimbos }) {
  if (!carimbos.length) {
    return (
      <NotaDiscreta style={{ margin: "6px 0 0" }}>
        Carimbos individuais indisponíveis neste agrupamento.
      </NotaDiscreta>
    );
  }
  return (
    <ListaCarimbos>
      {carimbos.map((c, idx) => (
        <li key={`${c.timestamp || ""}-${idx}`}>
          <Mono style={{ flex: "0 0 auto", fontSize: 11 }}>
            {formatarDataHoraCurta(c.timestamp)}
          </Mono>
          <span className="quem" title={c.operador_nome || c.operador || ""}>
            {c.operador_nome || c.operador || "operador –"}
          </span>
          <span style={{ flex: "0 0 auto" }}>{rotuloCarimbo(c)}</span>
          {c.peca_numero && (
            <Mono style={{ flex: "0 0 auto", fontSize: 11 }}>
              {c.peca_numero}
            </Mono>
          )}
          {Boolean(c.eh_setup) && <Pill $tone="neutral">setup</Pill>}
          {Number(c.qtd_refugo) > 0 && (
            <Pill $tone="danger">refugo {Number(c.qtd_refugo)}</Pill>
          )}
        </li>
      ))}
    </ListaCarimbos>
  );
}

function EventoBipagens({ ev, expandido, onAlternar }) {
  const carimbos = Array.isArray(ev.dados?.carimbos) ? ev.dados.carimbos : [];
  const resumo = resumoBipagens(ev.dados);
  return (
    <>
      <ResumoBipagens
        type="button"
        aria-expanded={expandido}
        title={`${rotuloEtapaBipagem(ev.dados)} — ${resumo}`}
        onClick={onAlternar}
      >
        {expandido ? (
          <FiChevronDown size={12} aria-hidden="true" />
        ) : (
          <FiChevronRight size={12} aria-hidden="true" />
        )}
        <span className="resumo">{resumo}</span>
      </ResumoBipagens>
      {expandido && <CarimbosDoGrupo carimbos={carimbos} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// Bloco
// ---------------------------------------------------------------------------

export default function Timeline({ ambienteId, gatilhoRecarga }) {
  const { request } = useAuth();

  const [eventos, setEventos] = useState(null); // null = 1ª carga (skeleton)
  const [omitidos, setOmitidos] = useState(0);
  const [limite, setLimite] = useState(LIMITE_INICIAL);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [expandidos, setExpandidos] = useState(() => new Set());

  const carregar = useCallback(async () => {
    setErro(null);
    setCarregando(true);
    try {
      const resposta = await timelineAmbiente(request, ambienteId, { limite });
      const lista = Array.isArray(resposta?.eventos) ? resposta.eventos : [];
      setEventos(lista);
      setOmitidos(Number(resposta?.omitidos) || 0);
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar a linha do tempo.");
    } finally {
      setCarregando(false);
    }
  }, [request, ambienteId, limite]);

  // `gatilhoRecarga` na dependência é proposital: o Detalhe troca o objeto
  // do ambiente após cada action e o evento novo deve aparecer sem F5.
  useEffect(() => {
    carregar();
  }, [carregar, gatilhoRecarga]);

  // Mais recente primeiro — ordena defensivamente (não confia na ordem do
  // payload; empate mantém a ordem de chegada).
  const ordenados = useMemo(
    () =>
      [...(eventos || [])].sort((a, b) =>
        String(b.timestamp || "").localeCompare(String(a.timestamp || "")),
      ),
    [eventos],
  );

  const alternarExpandido = (chave) =>
    setExpandidos((prev) => {
      const proximo = new Set(prev);
      if (proximo.has(chave)) proximo.delete(chave);
      else proximo.add(chave);
      return proximo;
    });

  return (
    <Card>
      <SectionTitle>Linha do tempo</SectionTitle>

      {eventos === null && !erro && <Shimmer $h={160} />}

      {erro && (
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>
            Não foi possível carregar a linha do tempo. {erro}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      )}

      {!erro && eventos !== null && ordenados.length === 0 && (
        <EmptyMsg>Nenhum evento registrado neste ambiente ainda.</EmptyMsg>
      )}

      {!erro && ordenados.length > 0 && (
        <>
          <Trilha>
            {ordenados.map((ev, idx) => {
              const chave = `${ev.tipo}-${ev.timestamp || ""}-${idx}`;
              const { icone: Icone, tone } = infoDoEvento(ev);
              const ehBipagens = ev.tipo === "bipagens_etapa";
              return (
                <ItemEvento key={chave} $tone={tone}>
                  <span className="ponto" aria-hidden="true">
                    <Icone size={13} />
                  </span>
                  <div className="conteudo">
                    <div className="topo">
                      <span
                        className="titulo"
                        title={ev.titulo || ev.tipo}
                      >
                        {ev.titulo
                          || (ehBipagens
                            ? `Bipagens — ${rotuloEtapaBipagem(ev.dados)}`
                            : ev.tipo)}
                      </span>
                      <Mono className="quando">
                        {formatarDataHoraCurta(ev.timestamp)}
                      </Mono>
                    </div>
                    {ehBipagens ? (
                      <EventoBipagens
                        ev={ev}
                        expandido={expandidos.has(chave)}
                        onAlternar={() => alternarExpandido(chave)}
                      />
                    ) : (
                      ev.detalhe && <p className="detalhe">{ev.detalhe}</p>
                    )}
                    {ev.ator_nome && (
                      <span className="ator">por {ev.ator_nome}</span>
                    )}
                  </div>
                </ItemEvento>
              );
            })}
          </Trilha>

          {omitidos > 0 && (
            <RodapeTimeline>
              <SecondaryButton
                type="button"
                disabled={carregando}
                onClick={() => setLimite((l) => l + INCREMENTO_LIMITE)}
              >
                {carregando
                  ? "Carregando…"
                  : `Carregar mais antigos (${omitidos})`}
              </SecondaryButton>
              <NotaDiscreta style={{ margin: 0 }}>
                mostrando os {ordenados.length} eventos mais recentes
              </NotaDiscreta>
            </RodapeTimeline>
          )}
        </>
      )}
    </Card>
  );
}
